import { useEffect, useState } from 'react';

interface MoneyInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * Money input that stores a numeric value (dollars.cents) and displays a
 * locale-neutral string. Uses a local string buffer so users can type mid-
 * decimal without the parent coercing intermediate states (e.g. "12." still
 * renders "12."). Emits <c>0</c> for empty / invalid input; the parent
 * should treat explicit validation as its own concern.
 */
export function MoneyInput({
  value,
  onChange,
  placeholder = '0.00',
  className,
  ariaLabel
}: MoneyInputProps) {
  const [text, setText] = useState(formatValue(value));

  // Keep the visible text in sync when `value` changes from above, but don't
  // clobber the buffer while the user is actively editing (text parses back
  // to the same numeric value).
  useEffect(() => {
    const parsed = parseValue(text);
    if (parsed !== value) setText(formatValue(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
      className={className ?? 'w-full border border-slate-300 rounded px-3 py-2 min-h-[44px] text-right'}
      placeholder={placeholder}
      value={text}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9.\-]/g, '');
        setText(raw);
        onChange(parseValue(raw));
      }}
      onBlur={() => setText(formatValue(value))}
    />
  );
}

function formatValue(v: number): string {
  if (!isFinite(v)) return '';
  return v.toFixed(2);
}

function parseValue(raw: string): number {
  if (raw.trim() === '' || raw === '-' || raw === '.') return 0;
  const n = Number(raw);
  return isFinite(n) ? n : 0;
}
